import React, { useState, useRef, useEffect, useCallback } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import { Search, LocateFixed } from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import { useTranslation } from "react-i18next";

const TILE_URLS = {
  dark: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
  light: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
};

function LocationMarker({ value, onChange }) {
  useMapEvents({
    click(e) {
      onChange({ latitude: e.latlng.lat, longitude: e.latlng.lng });
    },
  });

  if (!value) return null;
  return <Marker position={[value.latitude, value.longitude]} />;
}

function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target) {
      map.flyTo([target.lat, target.lng], 14, { duration: 1.2 });
    }
  }, [target, map]);
  return null;
}

function LocationPicker({ value, onChange }) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [flyTarget, setFlyTarget] = useState(null);
  const [locating, setLocating] = useState(false);
  const timerRef = useRef(null);
  const containerRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleSearch = useCallback((q) => {
    setQuery(q);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (q.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }

    timerRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=5`,
          { headers: { "Accept-Language": "en" } }
        );
        const data = await res.json();
        setResults(
          data.map((r) => ({
            name: r.name || r.display_name.split(",")[0],
            displayName: r.display_name,
            lat: parseFloat(r.lat),
            lng: parseFloat(r.lon),
          }))
        );
        setOpen(true);
      } catch {
        setResults([]);
      }
    }, 300);
  }, []);

  const handleSelect = (result) => {
    onChange({ latitude: result.lat, longitude: result.lng });
    setFlyTarget({ lat: result.lat, lng: result.lng });
    setQuery(result.name);
    setOpen(false);
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        onChange({ latitude, longitude });
        setFlyTarget({ lat: latitude, lng: longitude });
        setLocating(false);
      },
      () => {
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  return (
    <div className="w-full">
      {/* Search + Use My Location row */}
      <div className="flex gap-2 mb-2">
        <div ref={containerRef} className="relative flex-1">
          <div className="relative">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-th-muted pointer-events-none"
            />
            <input
              type="text"
              value={query}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder={t("location.searchPlace")}
              className="w-full pl-8 pr-3 py-2 text-base rounded-xl bg-th-surface text-th-text placeholder-th-muted border border-th-border outline-none focus:border-th-accent-border focus:ring-1 focus:ring-th-accent-border transition-all"
            />
          </div>
          {open && results.length > 0 && (
            <ul className="absolute z-[1000] left-0 right-0 mt-1 bg-th-card rounded-xl shadow-xl border border-th-border overflow-hidden max-h-48 overflow-y-auto">
              {results.map((r, i) => (
                <li key={i}>
                  <button
                    type="button"
                    onClick={() => handleSelect(r)}
                    className="w-full text-left px-3 py-2.5 text-sm text-th-text-sec hover:bg-th-surface active:bg-th-surface transition-colors border-b border-th-border last:border-0"
                  >
                    <span className="font-medium text-th-text">{r.name}</span>
                    <br />
                    <span className="text-xs text-th-muted truncate block">{r.displayName}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <button
          type="button"
          onClick={handleUseMyLocation}
          disabled={locating}
          className="shrink-0 px-3 py-2 rounded-xl bg-th-surface border border-th-border text-th-text hover:bg-th-card active:bg-th-card transition-colors disabled:opacity-50"
          title={t("location.useMyLocation")}
        >
          <LocateFixed size={18} className={locating ? "animate-pulse" : ""} />
        </button>
      </div>

      {/* Map */}
      <div className="w-full h-64 rounded-xl overflow-hidden border border-th-border">
        <MapContainer
          center={[20, 0]}
          zoom={2}
          style={{ width: "100%", height: "100%" }}
          zoomControl={false}
        >
          <TileLayer
            key={theme}
            url={TILE_URLS[theme]}
            attribution='&copy; OpenStreetMap &copy; CARTO'
          />
          <LocationMarker value={value} onChange={onChange} />
          <FlyTo target={flyTarget} />
        </MapContainer>
        {value ? (
          <p className="text-xs text-th-accent p-2 bg-th-surface font-mono">
            {value.latitude.toFixed(4)}, {value.longitude.toFixed(4)}
          </p>
        ) : (
          <p className="text-xs text-th-muted p-2 bg-th-surface font-mono">
            {t("location.tapMap")}
          </p>
        )}
      </div>
    </div>
  );
}

export default LocationPicker;
